import React from 'react';
import { Check, CircleX } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRecordBenefitsStatus } from './BenefitsStatusField';
import {
  BenefitsStatusResult,
  benefitsStateLabel,
  benefitsNoteTitle,
  formLeg,
  FormLeg,
} from './benefitsStatus';

// Front-page (board) benefits cell. Everything sits in a bordered box that
// matches the sibling status icons (DEC / Registry / Attestation). Terminal
// states collapse to a compact icon box; in-progress states are text + aging
// ("Sent · 3d") so an agent can see how long a case has been sitting. When the
// Survivor's Pension applies mid send/sign, both applications stack so partial
// completion is obvious.

// h-6 keeps the single-line box the same height as the 24px icon cells.
const BOX = 'inline-flex h-6 items-center justify-center rounded-md border px-1.5 text-[11px] font-medium whitespace-nowrap';

const LEG_BOX: Record<FormLeg['kind'], string> = {
  missing: 'border-red-400 text-red-600 bg-red-50',
  ready: 'border-blue-400 text-blue-600 bg-blue-50',
  sent: 'border-amber-500 text-amber-800 bg-amber-100',
  to_mail: 'border-purple-400 text-purple-700 bg-purple-50',
};

const AgeText: React.FC<{ leg: FormLeg; short?: boolean }> = ({ leg, short }) => (
  <span>
    {short ? leg.shortLabel : leg.label}
    {leg.age && (
      <span className={leg.stale ? 'ml-1 font-semibold text-red-600' : 'ml-1 opacity-70'}>
        · {leg.age}
      </span>
    )}
  </span>
);

const FormLine: React.FC<{ name: string; title: string; leg: FormLeg }> = ({
  name,
  title,
  leg,
}) => (
  <div className="flex items-center gap-1 text-[11px] leading-4 whitespace-nowrap" title={title}>
    <span className="text-muted-foreground/70 w-10 shrink-0">{name}</span>
    <span className={leg.textClass}>
      <AgeText leg={leg} short />
    </span>
  </div>
);

export const BenefitsListCell: React.FC = () => {
  const status: BenefitsStatusResult | undefined = useRecordBenefitsStatus();
  if (!status) {
    return (
      <span className={cn(BOX, 'border-gray-200 text-muted-foreground/40')}>–</span>
    );
  }

  const { state, perForm, survivorApplicable, benefitsType } = status;

  // Terminal — compact icon box (matches the sibling status icons).
  if (state === 'mailed' || state === 'completed') {
    return (
      <span
        title={
          state === 'completed' && status.note
            ? `Completed · ${benefitsNoteTitle(state, status.note)}`
            : state === 'mailed'
              ? 'Mailed'
              : 'Completed'
        }
        className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-transparent bg-green-600 text-white"
      >
        <Check className="h-3.5 w-3.5" strokeWidth={3} />
      </span>
    );
  }
  if (state === 'ineligible') {
    return (
      <span
        title={status.note ? benefitsNoteTitle(state, status.note) : 'Ineligible'}
        className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-gray-300 text-gray-400"
      >
        <CircleX className="h-3.5 w-3.5" strokeWidth={2.5} />
      </span>
    );
  }

  const dataReady = state !== 'missing_information';

  // Two applications in flight → show both legs in one bordered box so partial
  // completion is clear.
  if (survivorApplicable && (state === 'sent' || state === 'ready_to_mail')) {
    return (
      <div className="inline-flex flex-col items-start gap-0.5 rounded-md border border-border px-1.5 py-1">
        <FormLine
          name="Death"
          title="Death Benefit"
          leg={formLeg(perForm.death_benefit, dataReady)}
        />
        <FormLine
          name="Surv."
          title="Survivor's Pension"
          leg={formLeg(perForm.survivor_pension, dataReady)}
        />
      </div>
    );
  }

  // Single line — a bordered box coloured by state.
  if (state === 'missing_information') {
    return <span className={cn(BOX, LEG_BOX.missing)}>Missing info</span>;
  }
  if (state === 'ready_to_send') {
    return (
      <span
        className={cn(BOX, LEG_BOX.ready)}
        title={benefitsStateLabel('ready_to_send', benefitsType)}
      >
        {benefitsType === 'qpp' ? 'Ready' : 'Ready to send'}
      </span>
    );
  }
  // sent | ready_to_mail — borrow the death-benefit leg (survivor isn't in play).
  const leg = formLeg(perForm.death_benefit, dataReady);
  return (
    <span className={cn(BOX, LEG_BOX[leg.kind])}>
      <AgeText leg={leg} />
    </span>
  );
};

export default BenefitsListCell;
